import { GroupDetailsInfoBar } from "@components/group-details/group-details-info-bar";
import { BaseGroupDetailsProps } from "@custom-types/group-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { GroupManagersTable } from "@pages/group-details/group-managers-table";
import { useCompanyId } from "@context-providers/company-id-provider";
import { GroupMembersMobileView } from "@pages/group-details/group-members-mobile-view";
import { useGroupMemberBulkActions } from "@pages/group-details/use-group-member-bulk-actions";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { getExportComponent } from "@utils/member-utils";
import { MembersSort } from "@components/table/members/members-sort";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { MembersSortBy } from "@custom-types/member-types";
import { DEFAULT_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { isMembersSortBy } from "@custom-types/type-guards";

interface Props extends Partial<BaseGroupDetailsProps> {
  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;
}

/**
 * Contains the Managers tab of the group details
 */
export function GroupManagers({
  group,
  isLoading = false,
}: Props): JSX.Element {
  const companyId = useCompanyId();
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const { sortBy, setSortBy } = useSubjectsSortByQuery<MembersSortBy>({
    defaultSort: DEFAULT_MEMBERS_SORT_BY,
    isValidSort: isMembersSortBy,
  });

  const bulkActionButtons = useGroupMemberBulkActions({
    companyId,
    group,
  });

  const groupMembers = group?.members ?? [];
  const hasGroupMembers = !!groupMembers.length;

  return (
    <>
      <GroupDetailsInfoBar group={group} isLoading={isLoading} />

      <EntitiesToolbar
        toolbarEntityName="Member"
        numberOfRows={groupMembers.length}
        isLoading={isLoading}
        exportComponent={getExportComponent(
          groupMembers.length,
          bulkActionButtons.export
        )}
        sortActions={
          hasGroupMembers && (
            <MembersSort
              sortBy={sortBy}
              setSortBy={setSortBy}
              tableSubject="group"
            />
          )
        }
      />

      {isScreenSmOrSmaller && (
        <GroupMembersMobileView
          bulkActionButtons={bulkActionButtons}
          group={group}
          isLoading={isLoading}
          sortBy={sortBy}
        />
      )}

      {!isScreenSmOrSmaller && (
        <GroupManagersTable
          group={group}
          companyId={companyId}
          isLoading={isLoading}
          bulkActionButtons={bulkActionButtons}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      )}
    </>
  );
}
